import { ELEMENT_NAMES, round1 } from "./editorUtils.js";
import { BUILTIN_IMAGES, BUILTIN_IMAGE_NAMES } from "./assets.js";
import {
  IconTrash, IconCopy, IconLock, IconUnlock, IconChevronUp, IconChevronDown, IconPlus, IconX,
} from "./icons.jsx";

const inputCls =
  "w-full rounded border border-white/10 bg-navy-950 px-1.5 py-1 text-xs text-white outline-none focus:border-accent/60";
const labelCls = "mb-0.5 block text-[0.6875rem] font-medium text-slate-400";
const miniBtn = "rounded p-1 text-slate-400 hover:bg-white/10 hover:text-white";

function Num({ label, value, onChange, step = 0.5, min }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <input
        type="number"
        className={inputCls}
        value={value}
        step={step}
        min={min}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(round1(v));
        }}
      />
    </div>
  );
}

function Txt({ label, value, onChange }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Bool({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-300">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-cyan-400" />
      {label}
    </label>
  );
}

export function PropertiesPanel({
  template, element, onUpdateElement, onUpdateTemplate,
  onRemoveElement, onDuplicateElement, onReorderElement,
}) {
  return (
    <div className="flex w-64 shrink-0 flex-col gap-3 overflow-auto border-l border-white/10 bg-navy-900 p-3">
      {element ? (
        <ElementProps
          element={element}
          onUpdate={(p) => onUpdateElement(element.id, p)}
          onRemove={() => onRemoveElement(element.id)}
          onDuplicate={() => onDuplicateElement(element.id)}
          onReorder={(dir) => onReorderElement(element.id, dir)}
        />
      ) : (
        <PageProps template={template} onUpdateTemplate={onUpdateTemplate} />
      )}
    </div>
  );
}

// ---------- Propiedades de página (sin selección) ----------
function PageProps({ template, onUpdateTemplate }) {
  return (
    <>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Página</div>
      <div>
        <span className={labelCls}>Tamaño</span>
        <select
          className={inputCls}
          value={template.page.size}
          onChange={(e) => onUpdateTemplate({ page: { ...template.page, size: e.target.value } })}
        >
          <option value="A4">A4</option>
          <option value="A5">A5</option>
          <option value="Letter">Carta</option>
        </select>
      </div>
      <div>
        <span className={labelCls}>Orientación</span>
        <select
          className={inputCls}
          value={template.page.orientation}
          onChange={(e) => onUpdateTemplate({ page: { ...template.page, orientation: e.target.value } })}
        >
          <option value="portrait">Vertical</option>
          <option value="landscape">Apaisada</option>
        </select>
      </div>
      <div>
        <span className={labelCls}>Nombre de la plantilla</span>
        <input
          className={inputCls}
          value={template.name}
          onChange={(e) => onUpdateTemplate({ name: e.target.value })}
        />
      </div>
      <div className="rounded-md bg-white/5 p-2 text-[0.6875rem] leading-snug text-slate-400">
        Selecciona un elemento de la página para editar sus propiedades. Atajos: <b>Supr</b> elimina,{" "}
        <b>Ctrl+D</b> duplica, <b>flechas</b> mueven.
      </div>
    </>
  );
}

// ---------- Propiedades del elemento seleccionado ----------
function ElementProps({ element: el, onUpdate, onRemove, onDuplicate, onReorder }) {
  const upImage = (e) => {
    const f = e.target.files?.[0];
    if (!f || el.type !== "image") return;
    const reader = new FileReader();
    reader.onload = () => {
      // La imagen viaja dentro de la propia plantilla (base64). Se reduce a
      // un tamaño razonable para no reventar el límite de 2 MB del servidor:
      // una foto de móvil tal cual no llegaría a guardarse.
      const img = new Image();
      img.onload = () => {
        const MAX = 1000;
        const escala = Math.min(1, MAX / Math.max(img.width, img.height));
        if (escala >= 1 && f.type === "image/png") {
          onUpdate({ src: String(reader.result) });
          return;
        }
        const lienzo = document.createElement("canvas");
        lienzo.width = Math.round(img.width * escala);
        lienzo.height = Math.round(img.height * escala);
        lienzo.getContext("2d").drawImage(img, 0, 0, lienzo.width, lienzo.height);
        // PNG conserva la transparencia (logos); el resto, JPEG comprimido.
        const src =
          f.type === "image/png"
            ? lienzo.toDataURL("image/png")
            : lienzo.toDataURL("image/jpeg", 0.85);
        onUpdate({ src });
      };
      img.onerror = () => onUpdate({ src: String(reader.result) });
      img.src = String(reader.result);
    };
    reader.readAsDataURL(f);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {ELEMENT_NAMES[el.type]}
        </span>
        <div className="flex gap-1">
          <button title="Subir (encima)" className={miniBtn} onClick={() => onReorder(1)}>
            <IconChevronUp />
          </button>
          <button title="Bajar (debajo)" className={miniBtn} onClick={() => onReorder(-1)}>
            <IconChevronDown />
          </button>
          <button
            title={el.locked ? "Desbloquear" : "Bloquear"}
            className={miniBtn}
            onClick={() => onUpdate({ locked: !el.locked })}
          >
            {el.locked ? <IconLock className="h-4 w-4 text-amber-400" /> : <IconUnlock />}
          </button>
          <button title="Duplicar (Ctrl+D)" className={miniBtn} onClick={onDuplicate}>
            <IconCopy />
          </button>
          <button title="Eliminar (Supr)" className="rounded p-1 hover:bg-rose-500/10" onClick={onRemove}>
            <IconTrash className="h-4 w-4 text-rose-400" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Num label="X (mm)" value={el.x} onChange={(v) => onUpdate({ x: v })} min={0} />
        <Num label="Y (mm)" value={el.y} onChange={(v) => onUpdate({ y: v })} min={0} />
        <Num label="Ancho (mm)" value={el.w} onChange={(v) => onUpdate({ w: Math.max(3, v) })} min={3} />
        <Num label="Alto (mm)" value={el.h} onChange={(v) => onUpdate({ h: Math.max(2, v) })} min={2} />
      </div>

      {el.type === "text" && (
        <>
          <div>
            <span className={labelCls}>Texto</span>
            <textarea
              className={`${inputCls} h-16 resize-none`}
              value={el.text}
              onChange={(e) => onUpdate({ text: e.target.value })}
            />
          </div>
          <Num label="Tamaño (pt)" value={el.fontSize} onChange={(v) => onUpdate({ fontSize: v })} step={0.5} min={3} />
          <div className="flex items-center gap-3">
            <Bool label="Negrita" value={el.bold} onChange={(v) => onUpdate({ bold: v })} />
            <select className={inputCls} value={el.align} onChange={(e) => onUpdate({ align: e.target.value })}>
              <option value="left">Izquierda</option>
              <option value="center">Centrado</option>
              <option value="right">Derecha</option>
            </select>
          </div>
          <div>
            <span className={labelCls}>Color</span>
            <input
              type="color"
              className="h-7 w-full cursor-pointer bg-transparent"
              value={el.color}
              onChange={(e) => onUpdate({ color: e.target.value })}
            />
          </div>
        </>
      )}

      {(el.type === "field" || el.type === "textarea") && (
        <>
          <Txt label="Etiqueta" value={el.label} onChange={(v) => onUpdate({ label: v })} />
          <Txt label="Clave de dato (NEXOSPRO)" value={el.fieldKey} onChange={(v) => onUpdate({ fieldKey: v })} />
          <Num label="Tamaño letra (pt)" value={el.fontSize} onChange={(v) => onUpdate({ fontSize: v })} step={0.5} min={5} />
          <Bool label="Con recuadro" value={el.boxed} onChange={(v) => onUpdate({ boxed: v })} />
        </>
      )}

      {el.type === "checkbox" && (
        <>
          <Txt label="Texto" value={el.label} onChange={(v) => onUpdate({ label: v })} />
          <Txt label="Clave de dato" value={el.fieldKey} onChange={(v) => onUpdate({ fieldKey: v })} />
          <Num label="Tamaño letra (pt)" value={el.fontSize} onChange={(v) => onUpdate({ fontSize: v })} step={0.5} min={4} />
          <Bool label="Negrita" value={el.bold} onChange={(v) => onUpdate({ bold: v })} />
        </>
      )}

      {el.type === "image" && (
        <>
          <div>
            <span className={labelCls}>Imagen integrada</span>
            <select
              className={inputCls}
              value={BUILTIN_IMAGES[el.src] ? el.src : ""}
              onChange={(e) => e.target.value && onUpdate({ src: e.target.value })}
            >
              <option value="">(personalizada)</option>
              {Object.keys(BUILTIN_IMAGES).map((k) => (
                <option key={k} value={k}>{BUILTIN_IMAGE_NAMES[k]}</option>
              ))}
            </select>
          </div>
          <div>
            <span className={labelCls}>Subir imagen</span>
            <input type="file" accept="image/*" className="text-xs text-slate-400" onChange={upImage} />
          </div>
        </>
      )}

      {el.type === "line" && (
        <>
          <div>
            <span className={labelCls}>Orientación</span>
            <select className={inputCls} value={el.orientation} onChange={(e) => onUpdate({ orientation: e.target.value })}>
              <option value="h">Horizontal</option>
              <option value="v">Vertical</option>
            </select>
          </div>
          <Num label="Grosor (px)" value={el.thickness} onChange={(v) => onUpdate({ thickness: Math.max(0.5, v) })} step={0.5} min={0.5} />
          <div>
            <span className={labelCls}>Color</span>
            <input
              type="color"
              className="h-7 w-full cursor-pointer bg-transparent"
              value={el.color}
              onChange={(e) => onUpdate({ color: e.target.value })}
            />
          </div>
        </>
      )}

      {el.type === "rect" && (
        <>
          <Num label="Grosor borde (px)" value={el.borderWidth} onChange={(v) => onUpdate({ borderWidth: Math.max(0, v) })} step={0.5} min={0} />
          <div>
            <span className={labelCls}>Color borde</span>
            <input
              type="color"
              className="h-7 w-full cursor-pointer bg-transparent"
              value={el.borderColor}
              onChange={(e) => onUpdate({ borderColor: e.target.value })}
            />
          </div>
          <Txt label="Fondo (vacío = transparente)" value={el.background} onChange={(v) => onUpdate({ background: v })} />
        </>
      )}

      {el.type === "table" && <TableProps el={el} onUpdate={onUpdate} />}

      {el.type === "signature" && (
        <>
          <Txt label="Etiqueta" value={el.label} onChange={(v) => onUpdate({ label: v })} />
          <Txt label="Texto bajo la línea" value={el.sublabel} onChange={(v) => onUpdate({ sublabel: v })} />
        </>
      )}
    </>
  );
}

// ---------- Propiedades de tabla ----------
function TableProps({ el, onUpdate }) {
  const setCol = (i, patch) => {
    const columns = el.columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    onUpdate({ columns });
  };
  return (
    <>
      <Txt label="Título agrupado" value={el.groupTitle} onChange={(v) => onUpdate({ groupTitle: v })} />
      <Num label="Filas" value={el.rows} onChange={(v) => onUpdate({ rows: Math.max(1, Math.round(v)) })} step={1} min={1} />
      <Num label="Tamaño letra (pt)" value={el.headerFontSize} onChange={(v) => onUpdate({ headerFontSize: v })} step={0.5} min={4} />
      <Bool label="Numerar filas" value={el.showRowNumbers} onChange={(v) => onUpdate({ showRowNumbers: v })} />
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className={labelCls}>Columnas</span>
          <button
            className="flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-[0.6875rem] text-slate-200 hover:bg-white/20"
            onClick={() => onUpdate({ columns: [...el.columns, { title: "NUEVA", width: 0.2 }] })}
          >
            <IconPlus className="h-3 w-3" /> Añadir
          </button>
        </div>
        {el.columns.map((c, i) => (
          <div key={i} className="mb-1 flex items-center gap-1">
            <input className={`${inputCls} flex-1`} value={c.title} onChange={(e) => setCol(i, { title: e.target.value })} />
            <input
              type="number"
              className={`${inputCls} w-14`}
              value={c.width}
              step={0.05}
              min={0.05}
              title="Fracción del ancho"
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) setCol(i, { width: Math.max(0.05, v) });
              }}
            />
            <button
              className="rounded p-1 hover:bg-rose-500/10"
              disabled={el.columns.length <= 1}
              onClick={() => onUpdate({ columns: el.columns.filter((_, idx) => idx !== i) })}
            >
              <IconX className="h-3.5 w-3.5 text-rose-400" />
            </button>
          </div>
        ))}
        <div className="text-[0.625rem] text-slate-500">El número es la fracción del ancho total de cada columna.</div>
      </div>
    </>
  );
}
